/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useDispatch, useSelector } from "react-redux"
import { useContext, useState } from "react"
import { actions } from "store/duck/bulkUpload/actions"
import { AnyAction } from "redux"
import { AxiosAction } from "types/api"
import {
  ExportInventoryItemStatus,
  ExportMenuItemProperty,
  InventoryExportedFileResponse,
  ACTION_TYPE,
} from "types/delv-platform-api/inventoryCategory"
import {
  useScribeUploadAndPublishDrawer,
  SCRIBE_BULK_UPLOAD_LOADING_STATUS,
} from "pages/menu/detail/bulkUpload/scribe/useScribeUploadAndPublishDrawer"
import { AppState } from "store/store.type"
import {
  BULK_UPLOAD_TYPE,
  BulkUploadContext,
  DRAWER_STATES,
} from "../../BulkUploadContext"

export const useExportFileButtonProps = (
  selectedCategoryIDs?: string[],
  selectedItemStatus?: ExportInventoryItemStatus,
  selectedItemProperty?: ExportMenuItemProperty[],
) => {
  const dispatch = useDispatch()
  const [isFileExporting, setIsFileExporting] = useState<boolean>(false)
  const { trackDownloadCSVWithStatusClick } = useScribeUploadAndPublishDrawer()
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)

  const categories = useSelector((state: AppState) => state.menu.categories)
  const isCheckedAllCategory =
    selectedCategoryIDs?.length === categories?.length

  const {
    bulkUploadType,
    actionType,
    drawerStateTransitionDisabled,
    setDrawerState,
  } = useContext(BulkUploadContext)
  // no UI design or content yet, a generic error for exporting file failed
  const [
    exportFileErrorPopupVisible,
    setExportFileErrorPopupVisible,
  ] = useState<boolean>(false)

  const onClickExportFile = async () => {
    if (actionType === ACTION_TYPE.CREATE || !!selectedCategoryIDs?.length) {
      setIsFileExporting(true)

      // no need go through redux store
      const res = (await dispatch(
        actions.fetchInventoryExportedFile(
          {
            categoryIDs: selectedCategoryIDs,
            menuGroupID,
            itemStatus: selectedItemStatus,
            properties: selectedItemProperty,
            ...(bulkUploadType === BULK_UPLOAD_TYPE.MENU && {
              templateType: actionType,
            }),
          },
          merchantID,
          merchantGroupID,
          bulkUploadType,
        ) as AnyAction,
      )) as AxiosAction<InventoryExportedFileResponse>
      setIsFileExporting(false)

      if (res.error) {
        setExportFileErrorPopupVisible(true)
        trackDownloadCSVWithStatusClick(
          SCRIBE_BULK_UPLOAD_LOADING_STATUS.FAILURE,
          selectedItemProperty,
          selectedCategoryIDs,
          isCheckedAllCategory,
        )
        // no design
        return
      }

      const exportedFileUrl = res.payload.data?.downloadUrl
      if (exportedFileUrl) {
        // popup a window to choose a destination for the exported file
        trackDownloadCSVWithStatusClick(
          SCRIBE_BULK_UPLOAD_LOADING_STATUS.SUCCESS,
          selectedItemProperty,
          selectedCategoryIDs,
          isCheckedAllCategory,
        )
        window.location.assign(exportedFileUrl)
        if (!drawerStateTransitionDisabled) {
          setDrawerState(DRAWER_STATES.START)
        }
      } else {
        trackDownloadCSVWithStatusClick(
          SCRIBE_BULK_UPLOAD_LOADING_STATUS.FAILURE,
          selectedItemProperty,
          selectedCategoryIDs,
          isCheckedAllCategory,
        )
      }
    }
  }

  const getExportBtnEnabled = (): boolean => {
    if (actionType === ACTION_TYPE.UPDATE) {
      if (
        bulkUploadType === BULK_UPLOAD_TYPE.MENU &&
        !selectedItemProperty?.length
      )
        return false

      return !!selectedCategoryIDs?.length
    }

    return true
  }

  return {
    onClickExportFile,
    isExportBtnEnabled: getExportBtnEnabled(),
    isFileExporting,
    exportFileErrorPopupVisible,
    onCloseExportFileErrorPopup: () => setExportFileErrorPopupVisible(false),
  }
}
